import React, { useState, useEffect, useCallback } from 'react';
import { Table, Card, Select, Input, DatePicker, Button, Space, Tag, message } from 'antd';
import { SearchOutlined, ReloadOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import api from '../api';

const { RangePicker } = DatePicker;
const { Option } = Select;

const OperationLogPage = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20, total: 0 });
  const [filters, setFilters] = useState({
    module: '',
    operationType: '',
    operatorName: '',
    dateRange: null
  });

  const modules = [
    { value: '', label: '全部模块' },
    { value: 'AUTH', label: '认证' },
    { value: 'USER', label: '用户管理' },
    { value: 'ATTENDANCE', label: '签到管理' },
    { value: 'LEAVE', label: '请假管理' },
    { value: 'REPAIR', label: '报修管理' },
    { value: 'CLASS', label: '班级管理' }
  ];

  const operationTypes = [
    { value: '', label: '全部操作' },
    { value: 'CREATE', label: '创建' },
    { value: 'UPDATE', label: '更新' },
    { value: 'DELETE', label: '删除' },
    { value: 'LOGIN', label: '登录' },
    { value: 'LOGOUT', label: '登出' },
    { value: 'APPROVE', label: '审批通过' },
    { value: 'REJECT', label: '拒绝' },
    { value: 'ASSIGN', label: '分配' },
    { value: 'TRANSFER', label: '转交' },
    { value: 'COMPLETE', label: '完成' },
    { value: 'CANCEL', label: '取消' }
  ];

  const fetchLogs = useCallback(async (page = 0, size = 20) => {
    setLoading(true);
    try {
      const params = {
        page,
        size,
        module: filters.module || undefined,
        operationType: filters.operationType || undefined,
        operatorName: filters.operatorName || undefined
      };
      
      if (filters.dateRange && filters.dateRange[0] && filters.dateRange[1]) {
        params.startTime = filters.dateRange[0].startOf('day').toISOString();
        params.endTime = filters.dateRange[1].endOf('day').toISOString();
      }

      const res = await api.get('/logs', { params });
      setLogs(res.data.content || []);
      setPagination(prev => ({
        ...prev,
        current: page + 1,
        total: res.data.totalElements || 0
      }));
    } catch (err) {
      if (err.response?.status === 403) {
        message.error('无权限查看日志');
      } else {
        message.error('获取日志失败');
      }
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchLogs(0, pagination.pageSize);
  }, [fetchLogs, pagination.pageSize]);

  const handleSearch = () => {
    fetchLogs(0, pagination.pageSize);
  };

  const handleReset = () => {
    setFilters({ module: '', operationType: '', operatorName: '', dateRange: null });
    setTimeout(() => fetchLogs(0, pagination.pageSize), 0);
  };

  const handleTableChange = (pag) => {
    fetchLogs(pag.current - 1, pag.pageSize);
  };

  const getOperationTypeColor = (type) => {
    const colors = {
      CREATE: 'green',
      UPDATE: 'blue',
      DELETE: 'red',
      LOGIN: 'cyan',
      LOGOUT: 'default',
      APPROVE: 'green',
      REJECT: 'red',
      ASSIGN: 'purple',
      TRANSFER: 'orange',
      COMPLETE: 'green',
      CANCEL: 'default'
    };
    return colors[type] || 'default';
  };

  const getModuleColor = (module) => {
    const colors = {
      AUTH: 'blue',
      USER: 'purple',
      ATTENDANCE: 'cyan',
      LEAVE: 'orange',
      REPAIR: 'green',
      CLASS: 'magenta'
    };
    return colors[module] || 'default';
  };

  const columns = [
    {
      title: '时间',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 180,
      render: (text) => text ? dayjs(text).format('YYYY-MM-DD HH:mm:ss') : '-'
    },
    {
      title: '操作人',
      dataIndex: 'operatorName',
      key: 'operatorName',
      width: 100
    },
    {
      title: '角色',
      dataIndex: 'operatorRoleText',
      key: 'operatorRoleText',
      width: 100
    },
    {
      title: '模块',
      dataIndex: 'module',
      key: 'module',
      width: 100,
      render: (text, record) => (
        <Tag color={getModuleColor(text)}>{record.moduleText}</Tag>
      )
    },
    {
      title: '操作类型',
      dataIndex: 'operationType',
      key: 'operationType',
      width: 100,
      render: (text, record) => (
        <Tag color={getOperationTypeColor(text)}>{record.operationTypeText}</Tag>
      )
    },
    {
      title: '操作描述',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true
    },
    {
      title: '目标ID',
      dataIndex: 'targetId',
      key: 'targetId',
      width: 80
    }
  ];

  return (
    <div style={{ padding: 24 }}>
      <Card title="操作日志" style={{ marginBottom: 16 }}>
        <Space wrap style={{ marginBottom: 16 }}>
          <Select
            style={{ width: 150 }}
            value={filters.module}
            onChange={(v) => setFilters(prev => ({ ...prev, module: v }))}
            placeholder="选择模块"
          >
            {modules.map(m => <Option key={m.value} value={m.value}>{m.label}</Option>)}
          </Select>
          <Select
            style={{ width: 150 }}
            value={filters.operationType}
            onChange={(v) => setFilters(prev => ({ ...prev, operationType: v }))}
            placeholder="操作类型"
          >
            {operationTypes.map(t => <Option key={t.value} value={t.value}>{t.label}</Option>)}
          </Select>
          <Input
            style={{ width: 150 }}
            placeholder="操作人姓名"
            value={filters.operatorName}
            onChange={(e) => setFilters(prev => ({ ...prev, operatorName: e.target.value }))}
          />
          <RangePicker
            value={filters.dateRange}
            onChange={(dates) => setFilters(prev => ({ ...prev, dateRange: dates }))}
          />
          <Button type="primary" icon={<SearchOutlined />} onClick={handleSearch}>
            搜索
          </Button>
          <Button icon={<ReloadOutlined />} onClick={handleReset}>
            重置
          </Button>
        </Space>

        <Table
          columns={columns}
          dataSource={logs}
          rowKey="id"
          loading={loading}
          pagination={{
            ...pagination,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => `共 ${total} 条记录`
          }}
          onChange={handleTableChange}
          size="middle"
        />
      </Card>
    </div>
  );
};

export default OperationLogPage;
